import React, { forwardRef, HTMLAttributes, ReactElement, useMemo } from 'react'
import classNames from 'classnames'
import { Text, Col, Row, Badge, Icon } from '@evertel/web/ui'
import { ProfilePic } from './ProfilePic'
import { APIDataBlueUser } from '@evertel/types'

// ********************************************************************
// NOTE: this component should only be used where you are NOT expecting
// the user to be stored in the usersStore or come through MobX,
// like in user searches.
// For user's expected to be in the UsersStore use the UserTile component
// ********************************************************************

interface UserTileProps extends HTMLAttributes<HTMLDivElement> {
    user: APIDataBlueUser
    imageWidth?: number
    isGuest?: boolean
    isManager?: boolean
    subComponent?: ReactElement
    rightColComponent?: ReactElement
    className?: string
}

/**
 * Specifically for cases when user isn't in the store and is explicitly an object
 * eg: SearchSelect type dropdown data
 */
const UserTileRawData = forwardRef<HTMLDivElement, UserTileProps>(({
    user = {},
    imageWidth = 30,
    isGuest,
    isManager,
    subComponent,
    rightColComponent,
    className,
    ...otherProps
}, ref) => {

    const _className = classNames(
        'user-tile',
        'p-0',
        className
    )

    const displayName = useMemo(() => {
        if (!user?.id) return 'Unknown'

        if (user.firstName?.trim() || user.lastName?.trim()) {
            return (user.firstName + ' ' + user.lastName).trim()
        } else {
            return user.email
        }
    }, [user])

    return (
        <Row
            ref={ref}
            className={_className}
            {...otherProps}>
            <Col
                className="pe-3 flex-grow-0">
                <ProfilePic
                    userId={user?.id as number}
                    firstName={user?.firstName}
                    lastName={user?.lastName}
                    imageUrl={user?.publicImage}
                    width={imageWidth}
                    height={imageWidth}
                />
            </Col>
            <Col valign="start" style={{minWidth: 0}}>
                <Text
                    bold
                    className="text-truncate">
                    {displayName}
                    {(isManager) &&
                        <Badge
                            color="primary"
                            shape="rounded-pill"
                            className="ms-1 py-1">
                            Manager
                        </Badge>
                    }
                    {(isGuest) &&
                        <Badge
                            color="warning"
                            shape="rounded-pill"
                            className="ms-1 py-1">
                            Guest
                        </Badge>
                    }
                </Text>
                {subComponent}
            </Col>
            {(!!rightColComponent) &&
                <Col
                    align="right"
                    className="p-0">
                    {rightColComponent}
                </Col>
            }
        </Row>
    )
})

export { UserTileRawData }
